import { useEffect, useState } from "react";
import { fetchAdminAnalytics, fetchAdminFeedback } from "@/lib/api";
import { ANALYTICS } from "@/constants/testIds";
import { BarChart3, ThumbsUp, ThumbsDown, AlertTriangle, TrendingUp, Users, FileWarning, Calendar, Sparkles, Database } from "lucide-react";

const TYPE_LABEL = {
  wrong_category: "Wrong category",
  wrong_child: "Wrong child",
  wrong_priority: "Wrong priority",
  wrong_due_date: "Wrong due date",
  wrong_event: "Wrong event extraction",
  missed_extraction: "Missed extraction",
  other: "Other",
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([fetchAdminAnalytics(), fetchAdminFeedback()])
      .then(([a, f]) => { setData(a); setRecent(f); })
      .catch(e => setError(e?.response?.data?.detail || "Could not load analytics."));
  }, []);

  if (error) {
    return (
      <div className="fcc-card p-10 text-center">
        <AlertTriangle className="w-6 h-6 mx-auto text-[var(--fcc-orange)]" />
        <p className="mt-3 text-[var(--fcc-ink)] font-semibold">{error}</p>
        <p className="text-sm text-[var(--fcc-muted)] mt-2">Set <code className="bg-[var(--fcc-cream-2)] px-1.5 py-0.5 rounded">KIDHQ_ADMIN_EMAILS</code> in the backend env to restrict access.</p>
      </div>
    );
  }
  if (!data) return <div className="text-[var(--fcc-muted)]">Crunching the KidHQ training set…</div>;

  const t = data.totals;
  return (
    <div data-testid={ANALYTICS.page} className="space-y-10">
      <header className="fcc-card overflow-hidden grid lg:grid-cols-12">
        <div className="lg:col-span-8 p-8 sm:p-10">
          <div className="flex flex-wrap gap-3 items-center">
            <span className="fcc-pill fcc-chip-emma"><Database className="w-3 h-3" /> KidHQ proprietary dataset</span>
            <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-muted)]">{t.classification_version}</span>
          </div>
          <h1 className="font-display text-[40px] sm:text-[52px] leading-[1.05] text-[var(--fcc-ink)] mt-5">Classification analytics</h1>
          <p className="text-[var(--fcc-ink-soft)] mt-4 max-w-2xl leading-relaxed">Every 👍/👎 a parent gives is human-in-the-loop training data. We're not retraining yet — we're collecting cleanly so the eval set is ready when we are.</p>
        </div>
        <aside className="lg:col-span-4 p-7 sm:p-8 bg-[var(--fcc-cream-2)] border-l border-[var(--fcc-border)]">
          <p className="label-overline">Overall accuracy</p>
          <div data-testid={ANALYTICS.accuracyCard} className="mt-3 flex items-end gap-2">
            <span className="font-display text-6xl text-[var(--fcc-ink)] leading-none">{t.overall_accuracy == null ? "—" : `${Math.round(t.overall_accuracy * 100)}%`}</span>
            <span className="text-sm text-[var(--fcc-muted)] pb-2">of {t.feedback_total} ratings</span>
          </div>
          <p className="text-xs text-[var(--fcc-muted)] mt-3">{t.feedback_correct} correct · {t.feedback_incorrect} flagged · {t.users_giving_feedback} parents contributing</p>
        </aside>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Classifications" value={t.classifications} caption="All-time, all users" Icon={BarChart3} testId={ANALYTICS.totalCard} />
        <KPI label="Marked correct" value={t.feedback_correct} caption="Human 👍" Icon={ThumbsUp} tint="var(--fcc-green-soft)" />
        <KPI label="Flagged incorrect" value={t.feedback_incorrect} caption="Human 👎" Icon={ThumbsDown} tint="var(--fcc-red-soft)" />
        <KPI label="Contributors" value={t.users_giving_feedback} caption="Parents teaching the AI" Icon={Users} tint="var(--fcc-emma)" />
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <Panel title="Most common corrections" subtitle="What parents flag most often" Icon={TrendingUp}>
          {data.most_common_corrections.length === 0 ? <Empty msg="No corrections yet." /> : (
            <ul className="space-y-2">
              {data.most_common_corrections.map((r) => (
                <li key={r.type} data-testid={ANALYTICS.rowMostCorrected} className="flex items-center justify-between border border-[var(--fcc-border)] rounded-2xl p-3">
                  <span className="font-semibold text-[var(--fcc-ink)]">{TYPE_LABEL[r.type] || r.type}</span>
                  <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Most corrected senders" subtitle="Where the AI keeps tripping" Icon={Users}>
          {data.most_corrected_senders.length === 0 ? <Empty msg="Nothing yet." /> : (
            <ul className="space-y-2">
              {data.most_corrected_senders.map((r) => (
                <li key={r.sender_email} data-testid={ANALYTICS.rowSender} className="flex items-center justify-between border border-[var(--fcc-border)] rounded-2xl p-3">
                  <span className="font-mono text-sm text-[var(--fcc-ink)] truncate">{r.sender_email}</span>
                  <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <Panel title="Accuracy by category" subtitle="Eval signal for the next model" Icon={BarChart3}>
          {data.accuracy_by_category.length === 0 ? <Empty msg="Need more ratings." /> : (
            <div className="space-y-3">
              {data.accuracy_by_category.map(c => {
                const acc = c.accuracy == null ? null : Math.round(c.accuracy * 100);
                const tint = acc == null ? "var(--fcc-cream-2)" : acc >= 80 ? "var(--fcc-green-soft)" : acc >= 50 ? "#FCE4CF" : "var(--fcc-red-soft)";
                return (
                  <div key={c.category} className="border border-[var(--fcc-border)] rounded-2xl p-3">
                    <div className="flex justify-between text-sm">
                      <span className="font-semibold text-[var(--fcc-ink)]">{c.category}</span>
                      <span className="text-[var(--fcc-ink-soft)]">{acc == null ? "—" : `${acc}%`} · {c.total} ratings</span>
                    </div>
                    <div className="h-2 rounded-full bg-[var(--fcc-cream-2)] mt-2 overflow-hidden">
                      <div className="h-2 rounded-full" style={{ width: `${acc ?? 0}%`, background: tint }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
        <Panel title="Missed extractions" subtitle="Fields the parser kept missing" Icon={FileWarning}>
          {data.missed_extractions.length === 0 ? <Empty msg="No missed-extraction flags yet." /> : (
            <ul className="space-y-2">
              {data.missed_extractions.map((r) => (
                <li key={r.field} className="flex items-center justify-between border border-[var(--fcc-border)] rounded-2xl p-3">
                  <span className="font-mono text-sm text-[var(--fcc-ink)]">{r.field}</span>
                  <span className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </section>

      <section>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5" /> Latest feedback</h2>
        {recent.length === 0 ? <Empty msg="No ratings yet. Train KidHQ from the Detected page." /> : (
          <ul className="space-y-3">
            {recent.slice(0, 20).map(f => (
              <li key={f.id} className="fcc-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-[var(--fcc-ink)]">{f.verdict === "correct" ? "👍 Correct" : "👎 Incorrect"} · {(f.original_classification?.category) || "?"}</p>
                    <p className="text-xs text-[var(--fcc-muted)] mt-1 font-mono">{f.sender_email}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]"><Calendar className="w-3 h-3 inline mr-1" />{new Date(f.timestamp).toLocaleString()}</span>
                </div>
                {f.feedback_type?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {f.feedback_type.map(t => <span key={t} className="fcc-pill bg-[var(--fcc-red-soft)] text-[#8B241D]">{TYPE_LABEL[t] || t}</span>)}
                  </div>
                )}
                {f.notes && <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 italic">"{f.notes}"</p>}
                {Object.keys(f.correction_data || {}).length > 0 && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
                    {Object.entries(f.correction_data).map(([k, v]) => (
                      <div key={k} className="border border-[var(--fcc-border)] rounded-xl p-2">
                        <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{k}</span>
                        <p className="text-[var(--fcc-ink)] mt-0.5">{String(v)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function KPI({ label, value, caption, Icon, tint = "var(--fcc-cream-2)", testId }) {
  return (
    <div data-testid={testId} className="fcc-card p-6">
      <div className="flex items-start justify-between">
        <span className="text-sm font-semibold text-[var(--fcc-ink-soft)]">{label}</span>
        <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: tint }}>
          <Icon className="w-4 h-4 text-[var(--fcc-green)]" />
        </span>
      </div>
      <div className="font-display text-5xl text-[var(--fcc-ink)] mt-4">{value}</div>
      <p className="text-xs text-[var(--fcc-muted)] mt-1">{caption}</p>
    </div>
  );
}

function Panel({ title, subtitle, Icon, children }) {
  return (
    <div className="fcc-card p-7">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-[var(--fcc-green-soft)] flex items-center justify-center"><Icon className="w-5 h-5 text-[var(--fcc-green)]" /></span>
        <div>
          <p className="label-overline">{title}</p>
          <p className="text-xs text-[var(--fcc-muted)]">{subtitle}</p>
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <p className="text-sm text-[var(--fcc-muted)] text-center py-6">{msg}</p>;
}
